import { Button, Flex, Form } from 'antd';
import { Auth } from 'aws-amplify';
import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { IAuthProps } from '../../pages/Authentication';
import styles from './Auth.module.css';
import AuthNote from './AuthNote';
import { useDispatch, useSelector } from 'react-redux';
import { IState } from '../../graphql/types';
import { setUserLoggingIn } from '../../actions';
import { EmailFormInput, error, goto, PasswordFormInput } from './utils';
import Logo from '../Logo';
import { colors } from '../../theme';
import useUser from '../../hooks/useUser';

const SignIn: React.FC<IAuthProps> = ({
  form,
  onStateChange,
  authState,
  onAuthEvent,
}) => {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { refetch } = useUser();

  const userLoggingIn = useSelector<IState, boolean>(
    state => !!state.userLoggingIn
  );

  const login = React.useCallback(
    values => {
      dispatch(setUserLoggingIn(true));
      Auth.signIn(values.email, values.password)
        .then(user => {
          if (
            user.challengeName === 'SMS_MFA' ||
            user.challengeName === 'SOFTWARE_TOKEN_MFA'
          ) {
            onStateChange('confirmSignIn', user);
          } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
            onStateChange('requireNewPassword', user);
          } else if (user.challengeName === 'MFA_SETUP') {
            onStateChange('TOTPSetup', user);
          } else {
            Auth.verifiedContact(user).then(async data => {
              if (Object.keys(data.verified).length !== 0) {
                await refetch(user);
                onStateChange('signedIn', user);
              } else {
                user = { ...user, ...data };
                onStateChange('verifyContact', user);
              }
            });
          }
        })
        .catch(err2 => {
          if (err2.code === 'UserNotConfirmedException') {
            onStateChange('confirmSignUp', {
              username: values.email,
            });
          } else if (err2.code === 'PasswordResetRequiredException') {
            onStateChange('forgotPassword', {
              username: values.email,
            });
          } else {
            error(err2, authState, onAuthEvent);
          }

          dispatch(setUserLoggingIn(false));
        });
    },
    [authState, onStateChange, onAuthEvent, dispatch, refetch]
  );

  return (
    <div className={styles.signUpForm}>
      <div style={{ width: '128px', margin: 'auto', marginBottom: '48px' }}>
        <Logo />
      </div>
      <Form onFinish={login} form={form} layout="vertical" requiredMark={false}>
        <EmailFormInput />
        <PasswordFormInput newPassword={false} />

        <Flex vertical={true} justify={'center'} align={'center'}>
          <Form.Item style={{ marginBottom: 0, width: '100%' }}>
            <Button
              type="primary"
              block={true}
              htmlType="submit"
              loading={userLoggingIn}
              disabled={userLoggingIn}
              size={'large'}
            >
              Sign In
            </Button>
          </Form.Item>
          <Button
            type={'text'}
            onClick={goto('forgotPassword', push, pathname, onStateChange)}
            style={{ marginTop: 8, color: colors.black3 }}
          >
            Forgot Password?
          </Button>
        </Flex>
      </Form>
      <Flex vertical={true} align={'center'}>
        <AuthNote />
        <Button
          style={{
            width: 'fit-content',
            margin: '48px auto',
            boxShadow: 'none',
          }}
          href={`${process.env.REACT_APP_BASE_URL}/signup`}
          block={false}
        >
          Need an account? Sign up here.
        </Button>
      </Flex>
    </div>
  );
};

export default SignIn;
